/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
  Typography,
  useTheme,
  Box,
  Grid,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid, Header } from 'components';
import { colorTheme } from 'theme';

import { useEffect, useState } from 'react';
import {
  getContributionHoldingById,
  getMonthWiseContributionHoldingById,
} from 'service';
import { exportExcelFileNames, holdingConstants } from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { UnitFormater } from 'utils/GeneralUtil';
import {
  exportMonthWiseHoldings,
  fetchExportHoldingData,
} from 'service/CustomerService';
import CustomPagination from 'components/customDataGrid/CustomPagination';
import { useAppDispatch, type RootState } from 'store';
import { LoadingButton } from '@mui/lab';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
// import { dateFormatter } from 'pages/employeesDetails/planDetails/userWithdrawalRequests';

export interface HoldingsListInterface {
  contractId: number;
  fundId: number;
  fundName: string;
  totalUnitsAllocated: string;
  currentPrice: string;
}

export interface MonthWiseHoldingsListInterface {
  type: string;
  contractId: number;
  fundId: number;
  fundName: string;
  totalUnitsAllocated: string;
  currentPrice: string;
  buy: {
    type?: string;
    createdAt?: string;
    fund1?: string;
    fund2?: string;
    fund3?: string;
    fund4?: string;
    fund5?: string;
    fund6?: string;
    fund1Units?: string;
    fund2Units?: string;
    fund3Units?: string;
    fund4Units?: string;
    fund5Units?: string;
    fund6Units?: string;
    total?: string;
  };
  sell: {
    type?: string;
    createdAt?: string;
    fund1?: string;
    fund2?: string;
    fund3?: string;
    fund4?: string;
    fund5?: string;
    fund6?: string;
    fund1Units?: string;
    fund2Units?: string;
    fund3Units?: string;
    fund4Units?: string;
    fund5Units?: string;
    fund6Units?: string;
    total?: string;
  };
  fees: {
    month?: string;
    type?: string;
    createdAt?: string;
    fund1?: string;
    fund2?: string;
    fund3?: string;
    fund4?: string;
    fund5?: string;
    fund6?: string;
    fund1Units?: string;
    fund2Units?: string;
    fund3Units?: string;
    fund4Units?: string;
    fund5Units?: string;
    fund6Units?: string;
    total?: string;
  };
  closing: {
    month?: string;
    type?: string;
    createdAt?: string;
    fund1?: string;
    fund2?: string;
    fund3?: string;
    fund4?: string;
    fund5?: string;
    fund6?: string;
    fund1Units?: string;
    fund2Units?: string;
    fund3Units?: string;
    fund4Units?: string;
    fund5Units?: string;
    fund6Units?: string;
    total?: string;
  };
  reAllocation: {
    month?: string;
    type?: string;
    createdAt?: string;
    fund1?: string;
    fund2?: string;
    fund3?: string;
    fund4?: string;
    fund5?: string;
    fund6?: string;
    fund1Units?: string;
    fund2Units?: string;
    fund3Units?: string;
    fund4Units?: string;
    fund5Units?: string;
    fund6Units?: string;
    total?: string;
  };
}

export interface HoldingsFilters {
  startDate?: string | null;
  endDate?: string | null;
}

const CurrentHoldings = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [fundData, setHoldingsData] = useState<HoldingsListInterface[]>([]);
  const [monthWiseData, setMonthWiseData] = useState<
    MonthWiseHoldingsListInterface[]
  >([]);
  const { id } = useParams();
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [filterLoader, setFilterLoader] = useState<boolean>(false);
  const [monthlyExportLoader, setMonthlyExportLoader] =
    useState<boolean>(false);
  const [filters, setFilters] = useState<HoldingsFilters>({
    startDate: null,
    endDate: null,
  });
  const pageInfo = useSelector((state) => state?.pagination?.currentHoldings);
  const { allMasterFunds } = useSelector((state: RootState) => state.fund);

  const getMonthWiseData = async (): Promise<void> => {
    try {
      const res = await getMonthWiseContributionHoldingById(
        Number(id),
        Number(pageInfo?.page) + 1,
        pageInfo?.size,
        filters,
      );
      setMonthWiseData(res?.result);
      setPageCount(res?.count);
    } catch (error) {
      console.log('error fetching month wise holdings', err);
    }
  };

  useEffect(() => {
    void getContributionHoldingById(
      Number(id),
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
    )
      .then((res) => {
        const { basicInfo } = res as { basicInfo: HoldingsListInterface[] };
        setHoldingsData(basicInfo);
      })
      .catch((err) => {
        console.log('error view holding', err);
      });
  }, [id]);

  useEffect(() => {
    void getMonthWiseData();
  }, [id, pageInfo, pageInfo?.page, pageInfo.pageSize]);

  const downloadExcelFile = async (): Promise<void> => {
    try {
      const { url, filename } = await fetchExportHoldingData(id as string);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      dispatch(
        setSnackBarSuccess({
          snackBarMessage:
            strings.displayText.currentHoldingsReportExportSuccess,
        }),
      );
    } catch (error) {
      console.log('error', error);
      dispatch(
        setSnackBarFailed({
          snackBarMessage:
            strings.displayText.currentHoldingsReportExportFailed,
        }),
      );
    }
  };

  const downloadMonthWiseContribution = async (): Promise<void> => {
    setMonthlyExportLoader(true);

    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const dateDifference =
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

      if (dateDifference > 60) {
        setMonthlyExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.dateExceeded,
          }),
        );
        return;
      }
    }
    if (!filters.startDate || !filters.endDate) {
      setMonthlyExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.selectDateRange,
        }),
      );
      return;
    }

    try {
      const url = await exportMonthWiseHoldings(Number(id), filters);
      const link = document.createElement('a');
      link.href = url as string;
      link.download = exportExcelFileNames.MONTH_WISE_HOLDINGS;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage:
            strings.displayText.monthlyHoldingsReportExportSuccess,
        }),
      );
    } catch (error) {
      console.error('Error exporting month wise holdings:', error);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage:
            strings.displayText.monthlyHoldingsReportExportFailed,
        }),
      );
    } finally {
      setMonthlyExportLoader(false);
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: holdingConstants.fundName,
      flex: 2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.description} placement="top">
          <Typography
            variant="h5"
            color={colors.gray[100]}
            fontSize={14}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {row?.shortDescription}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: holdingConstants.closePrice,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.closePrice}
        </Typography>
      ),
      flex: 0.7,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row.currentPrice).toFixed(2))}
        </Typography>
      ),
    },
    {
      field: holdingConstants.totalUnitsAllocated,
      flex: 1,
      align: 'right',
      headerAlign: 'center',
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalAllocatedUnits}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {UnitFormater(Number(row.totalUnitsAllocated))}
        </Typography>
      ),
    },
    {
      field: holdingConstants.totalValue,
      flex: 1,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsUnitsValueColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(
            Number(row.currentPrice) * Number(row.totalUnitsAllocated),
          )}
        </Typography>
      ),
    },
  ];
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const transformType = (type) => {
    if (type === 'Allocation' || type === 'Sale') {
      return 'Purchase';
    } else if (type === 'Fee') {
      return 'Fee';
    } else if (type === 'ReAllocation') {
      return 'Re-allocation';
    }
    return type || '-';
  };

  const styles = {
    paperContainer: {
      width: '100%',
      padding: '10px',
      marginBottom: '20px',
    },
    emptyTransactionContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '70vh',
      width: '100%',
    },
    emptyTransactionText: {
      fontFamily: 'Inter!important',
      color: '#838383',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.2,
    },
    gridContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
      border: '1px solid gray',
      borderRadius: 2,
      borderBottom: '1px solid grey',
      boxShadow: '5px',
      padding: '20px',
      marginBottom: '100px',
      backgroundColor: colors.primaryBg[900],
      borderColor: colors.gray[800],
      '& .MuiDataGrid-cell:hover': {
        color: 'primary.main',
      },
      '& .MuiDataGrid-row': {
        color: colors.gray[800],
        borderTop: 1,
      },
    },
    heading: {
      fontSize: '14px',
      color: colors.gray[100],
      padding: '10px',
      marginBottom: '20px',
      fontWeight: 'bold',
      width: 250,
      textAlign: 'right',
    },
    heading1: {
      fontSize: '14px',
      color: colors.gray[100],
      padding: '10px',
      marginBottom: '20px',
      fontWeight: 'bold',
      width: 250,
      textAlign: 'left',
    },
    heading2: {
      fontSize: '14px',
      color: colors.gray[100],
      padding: '10px',
      marginBottom: '20px',
      fontWeight: 'bold',
      width: 250,
      textAlign: 'center',
    },

    bodyContainerWidth: {
      width: 250,
      textAlign: 'right',
    },
    bodyContainerWidth2: {
      width: 250,
      textAlign: 'center',
    },
    bodyContainerWidth1: {
      width: 300,
      display: 'flex',
      textAlign: 'center',
      marginLeft: '10px',
    },
    headingContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    bodyContainer: {
      padding: 15,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '100%',
    },
    bodyContainerGraded: {
      padding: 15,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      width: '100%',
      backgroundColor: 'lightgrey',
    },
    bodyContainer1: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    },
    bodyContainer2: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'right',
      alignItems: 'right',
      textAlign: 'center',
    },
    footerContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginTop: '20px',
      marginBottom: '10px',
    },
  };

  return (
    <>
      {fundData.length > 0 ? (
        <>
          <CustomDataGrid
            title="Current Holdings"
            isShowExport={true}
            onExportClick={() => {
              void downloadExcelFile();
            }}
            columns={columns}
            rows={fundData as GridRowData[]}
            getRowId={(row: GridRowData) => `${String(row.fundName)}`}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            tableHeight={'50vh'}
            hideFooter={true}
          />
          <Grid sx={styles.paperContainer}>{'  '}</Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              marginTop: '50px',
              gap: '40px',
            }}
          >
            <Header title={strings.displayText.monthlyHoldings} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '40px',
              }}
            >
              <TextField
                label={strings.displayText.startDate}
                type="date"
                value={filters.startDate}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }));
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label={strings.displayText.endDate}
                type="date"
                value={filters.endDate}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }));
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <LoadingButton
                variant="contained"
                sx={{
                  height: 40,
                  bgcolor: colors.greenAccent[500],
                  borderRadius: 2,
                  textTransform: 'none',
                }}
                loading={filterLoader}
                onClick={() => {
                  setFilterLoader(false);
                  void getMonthWiseData();
                }}
              >
                {strings.displayText.filter}
              </LoadingButton>
              <LoadingButton
                loading={monthlyExportLoader}
                variant="contained"
                sx={{
                  height: 40,
                  bgcolor: colors.greenAccent[500],
                  borderRadius: 2,
                  textTransform: 'none',
                }}
                onClick={() => {
                  void downloadMonthWiseContribution();
                }}
              >
                Export
              </LoadingButton>
            </Box>
          </Box>
          {monthWiseData.length > 0 ? (
            <>
              <Grid container sx={styles.gridContainer}>
                <Grid style={styles.headingContainer}>
                  <Grid style={styles.heading2}>
                    {strings.displayText.monthColumn}
                  </Grid>
                  <Grid style={styles.heading1}>
                    {strings.displayText.type}
                  </Grid>
                  {allMasterFunds.map((fund, index) => (
                    <Grid key={fund.fundId} style={styles.heading1}>
                      <Tooltip title={fund.description} placement="top">
                        <Typography
                          variant="h5"
                          color={colors.gray[100]}
                          fontSize={14}
                          sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            fontWeight: 'bold',
                          }}
                        >
                          {fund?.shortDescription}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  ))}
                  <Grid style={styles.heading2}>
                    {strings.displayText.total}
                  </Grid>
                </Grid>

                {monthWiseData.map((val, index) => {
                  return (
                    <>
                      <Grid
                        key={index}
                        style={
                          index % 2 === 0
                            ? styles.bodyContainerGraded
                            : styles.bodyContainer
                        }
                      >
                        <Grid style={styles.bodyContainer1}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.buy.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {transformType(val.buy.type)}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.buy.fund1))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.buy.fund2))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.buy.fund3))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.buy.fund4))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.buy.fund5))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.buy.fund6))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(
                              Number(val.buy.fund1 ?? 0) +
                                Number(val.buy.fund2 ?? 0) +
                                Number(val.buy.fund3 ?? 0) +
                                Number(val.buy.fund4 ?? 0) +
                                Number(val.buy.fund5 ?? 0) +
                                Number(val.buy.fund6 ?? 0),
                            )}
                          </Grid>
                        </Grid>
                        <Grid style={styles.bodyContainer2}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.buy.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {'Units'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.buy.fund1Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.buy.fund2Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.buy.fund3Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.buy.fund4Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.buy.fund5Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.buy.fund6Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth} />
                        </Grid>
                      </Grid>
                      <Grid
                        key={index}
                        style={
                          index % 2 === 0
                            ? styles.bodyContainerGraded
                            : styles.bodyContainer
                        }
                      >
                        <Grid style={styles.bodyContainer1}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.sell.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {transformType(val.sell.type)}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.sell.fund1))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.sell.fund2))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.sell.fund3))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.sell.fund4))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.sell.fund5))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.sell.fund6))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(
                              Number(val.sell.fund1 ?? 0) +
                                Number(val.sell.fund2 ?? 0) +
                                Number(val.sell.fund3 ?? 0) +
                                Number(val.sell.fund4 ?? 0) +
                                Number(val.sell.fund5 ?? 0) +
                                Number(val.sell.fund6 ?? 0),
                            )}
                          </Grid>
                        </Grid>
                        <Grid style={styles.bodyContainer2}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.sell.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {'Units'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.sell.fund1Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.sell.fund2Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.sell.fund3Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.sell.fund4Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.sell.fund5Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.sell.fund6Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth} />
                        </Grid>
                      </Grid>
                      <Grid
                        key={index}
                        style={
                          index % 2 === 0
                            ? styles.bodyContainerGraded
                            : styles.bodyContainer
                        }
                      >
                        <>
                          <Grid style={styles.bodyContainer1}>
                            <Grid style={styles.bodyContainerWidth2}>
                              {val.fees.month ?? '-'}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth1}>
                              {transformType(val.fees.type)}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(Number(val.fees.fund1))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(Number(val.fees.fund2))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(Number(val.fees.fund3))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(Number(val.fees.fund4))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(Number(val.fees.fund5))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(Number(val.fees.fund6))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {CurrencyFormater(
                                Number(val.fees.fund1 ?? 0) +
                                  Number(val.fees.fund2 ?? 0) +
                                  Number(val.fees.fund3 ?? 0) +
                                  Number(val.fees.fund4 ?? 0) +
                                  Number(val.fees.fund5 ?? 0) +
                                  Number(val.fees.fund6 ?? 0),
                              )}
                            </Grid>
                          </Grid>
                          <Grid style={styles.bodyContainer2}>
                            <Grid style={styles.bodyContainerWidth2}>
                              {val.fees.month ?? '-'}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth1}>
                              {'Units'}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {UnitFormater(Number(val.fees.fund1Units))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {UnitFormater(Number(val.fees.fund2Units))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {UnitFormater(Number(val.fees.fund3Units))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {UnitFormater(Number(val.fees.fund4Units))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {UnitFormater(Number(val.fees.fund5Units))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth}>
                              {UnitFormater(Number(val.fees.fund6Units))}
                            </Grid>
                            <Grid style={styles.bodyContainerWidth} />
                          </Grid>
                        </>
                      </Grid>
                      <Grid
                        key={`${index}-realloc`}
                        style={
                          index % 2 === 0
                            ? styles.bodyContainerGraded
                            : styles.bodyContainer
                        }
                      >
                        <Grid style={styles.bodyContainer1}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.reAllocation.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {transformType(val.reAllocation.type)}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.reAllocation.fund1))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.reAllocation.fund2))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.reAllocation.fund3))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.reAllocation.fund4))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.reAllocation.fund5))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.reAllocation.fund6))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(
                              Number(val.reAllocation.fund1 ?? 0) +
                                Number(val.reAllocation.fund2 ?? 0) +
                                Number(val.reAllocation.fund3 ?? 0) +
                                Number(val.reAllocation.fund4 ?? 0) +
                                Number(val.reAllocation.fund5 ?? 0) +
                                Number(val.reAllocation.fund6 ?? 0),
                            )}
                          </Grid>
                        </Grid>

                        <Grid style={styles.bodyContainer2}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.reAllocation.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {strings.displayText.units}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.reAllocation.fund1Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.reAllocation.fund2Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.reAllocation.fund3Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.reAllocation.fund4Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.reAllocation.fund5Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.reAllocation.fund6Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth} />
                        </Grid>
                      </Grid>
                      <Grid
                        key={index}
                        style={
                          index % 2 === 0
                            ? styles.bodyContainerGraded
                            : styles.bodyContainer
                        }
                      >
                        {/* Needed for future */}
                        {/* <Grid style={styles.bodyContainer1}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.closing.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {transformType(val.closing.type)}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.closing.fund1))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.closing.fund2))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.closing.fund3))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.closing.fund4))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.closing.fund5))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(Number(val.closing.fund6))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {CurrencyFormater(
                              Number(val.closing.fund1 ?? 0) +
                                Number(val.closing.fund2 ?? 0) +
                                Number(val.closing.fund3 ?? 0) +
                                Number(val.closing.fund4 ?? 0) +
                                Number(val.closing.fund5 ?? 0) +
                                Number(val.closing.fund6 ?? 0),
                            )}
                          </Grid>
                        </Grid> */}
                        <Grid style={styles.bodyContainer2}>
                          <Grid style={styles.bodyContainerWidth2}>
                            {val.closing.month ?? '-'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth1}>
                            {'Closing Units'}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.closing.fund1Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.closing.fund2Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.closing.fund3Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.closing.fund4Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.closing.fund5Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(Number(val.closing.fund6Units))}
                          </Grid>
                          <Grid style={styles.bodyContainerWidth}>
                            {UnitFormater(
                              Number(val.closing.fund1Units ?? 0) +
                                Number(val.closing.fund2Units ?? 0) +
                                Number(val.closing.fund3Units ?? 0) +
                                Number(val.closing.fund4Units ?? 0) +
                                Number(val.closing.fund5Units ?? 0) +
                                Number(val.closing.fund6Units ?? 0),
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  );
                })}
                <Box sx={styles.footerContainer}>
                  <CustomPagination
                    rowsPerPageOptions={[5, 10, 20]}
                    currentPage="currentHoldings"
                    totalCount={pageCount}
                  />
                </Box>
              </Grid>
            </>
          ) : (
            <Box sx={styles.emptyTransactionContainer}>
              <Typography sx={styles.emptyTransactionText}>
                {strings.displayText.noRows}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box sx={styles.emptyTransactionContainer}>
          <Typography sx={styles.emptyTransactionText}>
            {strings.displayText.NotHoldingsThere}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default CurrentHoldings;
